import { BellIcon, EyeIcon, PersonAddIcon, PlusIcon, TrashIcon } from '@primer/octicons-react';
import { Box, Tooltip, useConfirm } from '@primer/react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clubApi } from '../../api/clubApi';
import Button from '../../components/ui/button/Button';
import DataTable from '../../components/ui/data-table';
import { IColumn } from '../../components/ui/data-table/DataTable';
import Text from '../../components/ui/text';
import { useCommonContext } from '../../contexts/common';
import { NotifyHelper } from '../../helpers/toast';
import { IClubPeriod } from '../../models/response/club/IClubPeriod';
import ClubPeriodDetailModal from './ClubPeriodDetailModal';
import moment from 'moment';

const ClubPeriodPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [dataSource, setDataSource] = useState<IClubPeriod[]>([]);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [selectedId, setSelectedId] = useState<number>(0);
    const { translate } = useCommonContext();
    const confirm = useConfirm();
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        handleReloadAsync();
    }, [])
    const handleReloadAsync = async () => {
        setIsLoading(true);
        const res = await clubApi.periodSelect();
        setIsLoading(false)
        if (res.is_success) {
            setDataSource(res.data)
        } else {
            NotifyHelper.Error(res.message ?? "Có lỗi")
        }
    }
    const handleSendNotify = async (id: number) => {
        if (await confirm({
            content: "Gửi thông báo tới PHHS",
            title: "Xác nhận",
            cancelButtonContent: "Đóng",
            confirmButtonContent: "Gửi thông báo",
            confirmButtonType: "primary"
        })) {
            setIsSaving(true);
            const res = await clubApi.sendNotify(id);
            setIsSaving(false);
            if (res.is_success) {
                handleReloadAsync();
                NotifyHelper.Success("Success");
            } else {
                NotifyHelper.Error(res.message ?? "");
            }
        }
    }
    const onDelete = async (clbId: number) => {
        if (await confirm({
            content: "Bạn có chắc chắn muốn xóa nội dung này",
            title: "Xóa câu lạc bộ",
            cancelButtonContent: "Không xóa",
            confirmButtonContent: "Xóa",
            confirmButtonType: "danger"
        })) {
            const res = await clubApi.periodDelete(clbId);
            if (res.is_success) {
                handleReloadAsync();
                NotifyHelper.Success("Success");
            } else {
                NotifyHelper.Error(res.message ?? "Error")
            }
        }
    }
    const columns = useMemo((): any => {
        let result: IColumn[] = [
            {
                dataField: "name",
                minWidth: "180px",
                isMainColumn: true,
                caption: `${translate('Base.Label.Name')}`,
                cellRender: (data: IClubPeriod) => {
                    return (
                        <Box
                            sx={{ cursor: "pointer", color: "blue" }}
                            onClick={() => {
                                setSelectedId(data.id);
                                setIsOpenModal(true);
                            }}
                        >
                            {data.name}
                        </Box>
                    );
                }
            },
            {
                dataField: "name_en",
                minWidth: "180px",
                isMainColumn: true,
                caption: `${translate('Base.Label.NameEn')}`,
                cellRender: (data: IClubPeriod) => {
                    return (
                        <Box
                            sx={{ cursor: "pointer", color: "blue" }}
                            onClick={() => {
                                setSelectedId(data.id);
                                setIsOpenModal(true);
                            }}
                        >
                            {data.name_en}
                        </Box>
                    );
                }
            },
            {
                dataField: "registration_start_at",
                minWidth: "180px",
                caption: `${translate('Đăng ký từ')}`,
                cellRender: (data: IClubPeriod) => {
                    return (
                        <Box
                        >
                            {moment(data.registration_start_at).format('DD/MM/YYYY HH:mm')}
                        </Box>
                    );
                }
            },
            {
                dataField: "registration_end_at",
                minWidth: "180px",
                caption: `${translate('Đăng ký đến')}`,
                cellRender: (data: IClubPeriod) => {
                    return (
                        <Box
                        >
                            {moment(data.registration_end_at).format('DD/MM/YYYY HH:mm')}
                        </Box>
                    );
                }
            },
            {
                dataField: "is_published",
                width: "150px",
                caption: `${translate('Base.Label.Status')}`,
                cellRender: (data: IClubPeriod) => {
                    return (
                        <>
                            <Box sx={{ display: "flex", flexDirection: "row" }}>
                                {data.is_published &&
                                    <Tooltip text='Đã publish, PHHS có thể nhìn thấy'>
                                        <Button variant='invisible' text='Đã publish' leadingVisual={EyeIcon} />
                                    </Tooltip>
                                }
                                {!data.is_published &&
                                    <Tooltip text='Chưa publish, PHHS chưa thể nhìn thấy'>
                                        <Button variant='danger' text='Chưa publish' leadingVisual={EyeIcon} />
                                    </Tooltip>
                                }
                            </Box>
                        </>
                    );
                }
            },
            {
                dataField: "app_notify_id",
                width: "200px",
                caption: `${translate('Thông báo')}`,
                cellRender: (data: IClubPeriod) => {
                    return (
                        <>
                            <Box sx={{ display: "flex", flexDirection: "row" }}>
                                {(data?.app_notify_id ?? 0) > 0 &&
                                    <Tooltip text='Phụ huynh đã nhận được thông báo đăng ký về Meeting'>
                                        <Button variant='invisible' text='Đã Gửi thông báo' leadingVisual={BellIcon} />
                                    </Tooltip>
                                }
                                {(data?.app_notify_id ?? 0) <= 0 &&
                                    <Tooltip text='Chưa gửi thông báo tới PHHS'>
                                        <Button variant='danger' text='Chưa gửi thông báo' leadingVisual={BellIcon} onClick={() => {
                                            if (data.is_published) {
                                                handleSendNotify(data.id)
                                            } else {
                                                NotifyHelper.Error("Vui lòng publish Meeting trước")
                                            }
                                        }} />
                                    </Tooltip>
                                }
                            </Box>
                        </>
                    );
                }
            },
            {
                id: "cmd",
                width: "100px",
                caption: `${translate('Base.Label.Command')}`,
                cellRender: (data: IClubPeriod) => {
                    return (
                        <>
                            <Button icon={TrashIcon} aria-label='' variant='danger' onClick={() => {
                                onDelete(data.id)
                            }} />
                        </>
                    );
                }
            },
        ];

        return result;
    }, []);

    return (
        <Box sx={{
            p: 3
        }}>
            <Box>
                <DataTable
                    titleComponent={
                        <>
                            <Text text={`${translate("Đợt đăng ký câu lạc bộ")}`} sx={{
                                fontSize: "24px"
                            }} />
                        </>
                    }
                    subTitle={`${translate("Base.Label.TotalCount")}: ${(dataSource?.length ?? 0).toLocaleString()}`}
                    searchEnable
                    isLoading={isLoading}
                    actionComponent={
                        <Box sx={{ display: "flex" }}>
                            <Button
                                text='Thêm mới'
                                leadingVisual={PlusIcon}
                                variant='primary'
                                onClick={() => {
                                    setSelectedId(0);
                                    setIsOpenModal(true);
                                }}
                            />
                        </Box>
                    }
                    columns={columns}
                    data={dataSource}
                    height={`${window.innerHeight - 250}px`}
                />
            </Box>
            {isOpenModal &&
                <ClubPeriodDetailModal
                    isOpen={isOpenModal}
                    id={selectedId}
                    onClose={() => setIsOpenModal(false)}
                    onSuccess={() => {
                        handleReloadAsync();
                    }}
                />
            }
        </Box>
    );
};

export default ClubPeriodPage;